import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { api } from "./api";

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const DAILY_LIMIT = 16;
const EMPTY_FORM = { projectid: "", hoursworked: "", description: "" };
// Time can only be entered, changed or deleted from this date on (the backend has the same rule).
const ENTRY_START_DATE = "2026-11-01";

const todayIso = () => new Date().toLocaleDateString("en-CA");
const currentMonth = () => todayIso().slice(0, 7);
const fmtHours = (h) => Number(Number(h || 0).toFixed(2));

function shiftMonth(month, delta) {
  const [y, m] = month.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function monthLabel(month) {
  const [y, m] = month.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

function dayLabel(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" });
}

// Every date of the month with its weekend flag (Sat / Sun).
function buildDays(month) {
  const [y, m] = month.split("-").map(Number);
  const count = new Date(y, m, 0).getDate();
  return Array.from({ length: count }, (_, i) => {
    const date = `${month}-${String(i + 1).padStart(2, "0")}`;
    const dow = new Date(y, m - 1, i + 1).getDay();
    return { date, isWeekend: dow === 0 || dow === 6 };
  });
}

const Icon = ({ children }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{children}</svg>
);
const TrashIcon = () => (
  <Icon>
    <path d="M3 6h18" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" />
  </Icon>
);
const PlusIcon = () => <Icon><path d="M12 5v14" /><path d="M5 12h14" /></Icon>;
const CheckIcon = () => <Icon><path d="M20 6 9 17l-5-5" /></Icon>;
const LockIcon = () => (
  <Icon><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></Icon>
);

// 4 h and under red, under 8 h orange, 8 to 16 h green.
const hoursLevel = (h) => (h >= 8 ? "full" : h > 4 ? "partial" : "low");

// Grow the description box with its text; empty it stays one line like a normal input.
const autoGrow = (el) => {
  el.style.height = "auto";
  el.style.height = `${el.scrollHeight + 2}px`;
};

export default function Timesheet({ userDetails, projects }) {
  const empid = userDetails.EmpID;
  const [month, setMonth] = useState(currentMonth);
  // null = no date open; the calendar shows only the dates until one is clicked.
  const [selected, setSelected] = useState(null);
  const calendarRef = useRef(null);
  const [monthHours, setMonthHours] = useState({});
  const [holidays, setHolidays] = useState({}); // { "2026-10-20": "Diwali" }
  const [entries, setEntries] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const days = buildDays(month);
  // Holidays are shown in red like weekends; only weekday holidays reduce the working days.
  const weekdayHolidays = days.filter((d) => !d.isWeekend && holidays[d.date]).length;
  const weekends = days.filter((d) => d.isWeekend).length;
  const workingDays = days.length - weekends - weekdayHolidays;
  // Leading blanks so the 1st lands under the right weekday (Monday first).
  const leading = (new Date(`${month}-01T00:00`).getDay() + 6) % 7;
  // Grid cell of the selected date; its entries open after the last date of that week row.
  const selectedCell = leading + Math.max(0, days.findIndex((d) => d.date === selected));
  const selectedCol = selectedCell % 7;
  const panelAfterCell = Math.min(selectedCell - selectedCol + 6, leading + days.length - 1);
  const today = todayIso();
  const isFuture = !!selected && selected > today;
  const isClosed = !!selected && selected < ENTRY_START_DATE;
  const dayTotal = entries.reduce((sum, e) => sum + Number(e.hoursworked), 0);
  const remaining = fmtHours(DAILY_LIMIT - dayTotal);

  const loadMonth = useCallback(() => {
    if (!empid) return;
    api.getMonthHours(empid, month).then(setMonthHours).catch((e) => setError(e.message));
  }, [empid, month]);

  const loadDay = useCallback(() => {
    if (!empid || !selected || selected > todayIso()) { setEntries([]); return; }
    api.getTimeEntry(empid, selected).then(setEntries).catch((e) => setError(e.message));
  }, [empid, selected]);

  useEffect(() => { loadMonth(); }, [loadMonth]);
  useEffect(() => {
    api
      .getHolidays(month)
      .then((list) => setHolidays(Object.fromEntries(list.map((h) => [h.date, h.name]))))
      .catch((e) => setError(e.message));
  }, [month]);
  useEffect(() => { loadDay(); }, [loadDay]);

  // A click anywhere outside the calendar (dates + the open entries) closes the open date.
  useEffect(() => {
    if (!selected) return undefined;
    const close = (e) => {
      if (calendarRef.current && !calendarRef.current.contains(e.target)) {
        setSelected(null);
        setForm(EMPTY_FORM);
        setError("");
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [selected]);

  const selectDay = (date) => {
    setSelected(date);
    setForm(EMPTY_FORM);
    setError("");
  };

  // Clicking the open date again closes it; another date opens that one instead.
  const toggleDay = (date) => selectDay(date === selected ? null : date);

  const changeMonth = (m) => {
    setMonth(m);
    selectDay(null);
  };

  const add = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      await api.addTimeEntry({
        empid,
        employeename: userDetails.EmpName,
        employeeemail: userDetails.EmpEmail,
        location: userDetails.Location,
        department: userDetails.Department,
        dateofentry: selected,
        reporting_manager_email: userDetails.ReportingManagerEmail,
        reporting_manager_name: userDetails.ReportingManagerName,
        reporting_manager_empid: userDetails.ReportingManagerEmpID,
        ...form,
      });
      setForm(EMPTY_FORM);
      loadDay();
      loadMonth();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    setError("");
    try {
      await api.deleteTimeEntry(empid, id);
      loadDay();
      loadMonth();
    } catch (err) {
      setError(err.message);
    }
  };

  // That day's entries, shown inside the calendar right below the selected date's week.
  const dayPanel = selected && (
    <div
      key={`panel-${selected}`}
      className="timesheet-day-panel"
      style={{ "--col": selectedCol }}
      role="region"
      aria-label={`Time entries for ${dayLabel(selected)}`}
    >
      {isFuture ? (
        <div className="timesheet-empty">
          <LockIcon />
          <p>Time entry is not allowed for future dates.</p>
        </div>
      ) : (
        <div className="timesheet-entry-box">
          {isClosed ? null : remaining > 0 ? (
            <form onSubmit={add}>
              <div className="timesheet-entry-title">New time entry</div>
              <div className="timesheet-entry-fields">
                <label className="timesheet-field">
                  <span>Project</span>
                  <select required value={form.projectid} onChange={(e) => setForm({ ...form, projectid: e.target.value })}>
                    <option value="">Select project…</option>
                    {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </label>
                <label className="timesheet-field timesheet-field-hours">
                  <span>Hours</span>
                  <input
                    required type="number" min="0.5" step="0.5" max={remaining}
                    placeholder={`Max ${remaining}`} value={form.hoursworked}
                    onChange={(e) => setForm({ ...form, hoursworked: e.target.value })}
                  />
                </label>
                <label className="timesheet-field timesheet-field-desc">
                  <span>Description</span>
                  <textarea
                    maxLength={255} rows={1}
                    placeholder="What did you work on? (optional)" value={form.description}
                    ref={(el) => el && autoGrow(el)}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                  />
                </label>
                <div className="timesheet-field timesheet-field-action">
                  <span aria-hidden="true">&nbsp;</span>
                  <button className="timesheet-primary" type="submit" disabled={saving}>
                    <PlusIcon /> {saving ? "Saving…" : "Add entry"}
                  </button>
                </div>
              </div>
            </form>
          ) : (
            <p className="timesheet-limit-note"><CheckIcon /> {DAILY_LIMIT}-hour limit reached for this day.</p>
          )}

          <div className="timesheet-table-wrap">
            <table className="timesheet-table">
              <thead>
                <tr>
                  <th className="timesheet-col-index">#</th>
                  <th>Project</th>
                  <th>Description</th>
                  <th className="timesheet-num">Hours</th>
                  <th className="timesheet-col-action" aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {entries.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="timesheet-table-empty">
                      {isClosed ? "No time entries for this day." : "No time entries yet. Add your first one above."}
                    </td>
                  </tr>
                ) : (
                  entries.map((x, i) => (
                    <tr key={x.id}>
                      <td className="timesheet-col-index">{i + 1}</td>
                      <td>
                        <span className="timesheet-project">{x.projectname}</span>
                      </td>
                      <td className="timesheet-description">{x.description}</td>
                      <td className="timesheet-num"><span className="timesheet-hours-pill">{fmtHours(x.hoursworked)} h</span></td>
                      <td className="timesheet-col-action">
                        {x.is_manager_approved ? (
                          <span className="timesheet-approved-tag" title="Approved by your manager">
                            <LockIcon /> Approved
                          </span>
                        ) : isClosed ? null : (
                          <button
                            className="timesheet-delete"
                            onClick={() => remove(x.id)}
                            title="Delete entry"
                            aria-label={`Delete entry ${i + 1}`}
                          >
                            <TrashIcon />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {error && <div className="timesheet-error">{error}</div>}
    </div>
  );

  return (
    <section className="timesheet-container">
      <div className="timesheet-head">
        <h2>My Timesheet</h2>
        <div className="timesheet-month-nav">
          <button onClick={() => changeMonth(shiftMonth(month, -1))} aria-label="Previous month">‹</button>
          <strong>{monthLabel(month)}</strong>
          <button onClick={() => changeMonth(shiftMonth(month, 1))} aria-label="Next month">›</button>
          <button onClick={() => changeMonth(currentMonth())}>Today</button>
        </div>
      </div>

      <div className="timesheet-stats">
        <div><span>Days in month</span><strong>{days.length}</strong></div>
        <div><span>Working days</span><strong>{workingDays}</strong></div>
        <div><span>Weekends</span><strong>{weekends}</strong></div>
        <div><span>Holidays</span><strong>{weekdayHolidays}</strong></div>
        <div><span>Hours this month</span><strong>{fmtHours(Object.values(monthHours).reduce((a, b) => a + b, 0))}</strong></div>
      </div>

      <div className="timesheet-legend">
        <span><i className="timesheet-swatch working" /> Working day</span>
        <span><i className="timesheet-swatch weekend" /> Weekend (Sat / Sun) / Holiday</span>
        <span><i className="timesheet-swatch low" /> 4 h and under</span>
        <span><i className="timesheet-swatch partial" /> Under 8 h</span>
        <span><i className="timesheet-swatch full" /> 8 – 16 h</span>
      </div>

      <div className="timesheet-calendar" ref={calendarRef}>
        {WEEKDAYS.map((w, i) => (
          <div key={w} className={`timesheet-weekday ${i >= 5 ? "weekend" : ""}`}>{w}</div>
        ))}
        {Array.from({ length: leading }, (_, i) => <div key={`b${i}`} />)}
        {days.map((d, i) => {
          const hours = monthHours[d.date] || 0;
          const future = d.date > today;
          const showProgress = !future;
          const holiday = holidays[d.date];
          const kind = holiday ? `Holiday: ${holiday}` : d.isWeekend ? "Weekend" : "Working day";
          return (
            <Fragment key={d.date}>
              <button
                className={`timesheet-day ${d.isWeekend || holiday ? "weekend" : "working"} ${future ? "future" : ""} ${d.date === selected ? "selected" : ""} ${d.date === today ? "today" : ""}`}
                title={future ? `${holiday ? `${kind} · ` : ""}Future date` : `${kind} · ${fmtHours(hours)} / ${DAILY_LIMIT} h`}
                aria-expanded={d.date === selected}
                onClick={() => toggleDay(d.date)}
              >
                <span className="timesheet-day-num">{Number(d.date.slice(8))}</span>
                {holiday && <span className="timesheet-holiday-name">{holiday}</span>}
                {showProgress && (
                  <span className="timesheet-day-progress">
                    <span className={`timesheet-hours-label ${hoursLevel(hours)}`}>
                      {fmtHours(hours)} hrs
                    </span>
                    <span className="timesheet-progress-track">
                      <span
                        className={`timesheet-progress-fill ${hoursLevel(hours)}`}
                        style={{ width: `${Math.min(100, (hours / DAILY_LIMIT) * 100)}%` }}
                      />
                    </span>
                  </span>
                )}
              </button>
              {leading + i === panelAfterCell && dayPanel}
            </Fragment>
          );
        })}
      </div>

      {/* With a date open its errors show inside the entries box. */}
      {!selected && error && <div className="timesheet-error">{error}</div>}
    </section>
  );
}
